import { GetTierIiReportQuery } from "generated-graphql/graphql";
import { useParams } from "react-router-dom";

import { useFacilityDetails } from "./useFacilityDetails";
import { useReportDetails } from "./useReportDetails";

type RequireFields<T, K extends keyof T> = Required<Pick<T, K>> & Partial<Omit<T, K>>;

type PartialTierIIReport = RequireFields<
  GetTierIiReportQuery["tierIIReport"],
  "facility" | "tenantId"
> & {
  reasonForReport?: string | null;
};

type FulfillmentDetailsData = {
  tierIIReport: PartialTierIIReport;
};

export function useFulfillmentDetails() {
  const { reportId, facilityId, reportingYear } = useParams<{
    reportId?: string;
    facilityId?: string;
    reportingYear?: string;
  }>();

  // existing hooks already handle the skip logic internally
  const reportQuery = useReportDetails();
  const facilityQuery = useFacilityDetails();

  const isUsingReport = !!reportId;
  const isUsingFacility = !!facilityId && !reportId;
  const activeQuery = isUsingReport ? reportQuery : facilityQuery;

  let data: FulfillmentDetailsData | undefined;

  if (isUsingReport && reportQuery.data) {
    data = reportQuery.data;
  } else if (
    isUsingFacility &&
    facilityQuery.data?.facility &&
    facilityQuery.data.facility.tenant &&
    facilityQuery.data.facility.tenantId
  ) {
    data = {
      tierIIReport: {
        facility: facilityQuery.data.facility,
        tenantId: facilityQuery.data.facility.tenantId,
        tenant: facilityQuery.data.facility.tenant,
      },
    };
  }

  // Get reporting year from report data or URL params
  const reportingYearValue =
    data?.tierIIReport.reportingYear ?? (reportingYear ? parseInt(reportingYear, 10) : undefined);

  const resolvedReportId = isUsingReport
    ? reportQuery.data?.tierIIReport?.id
    : facilityQuery.data?.tierIIReports?.items?.find(
        (item: { facilityId: string }) => item && item.facilityId === facilityId
      )?.id;

  const resolvedFacilityId = facilityId ?? data?.tierIIReport.facility?.id;
  const tenantId = data?.tierIIReport.tenantId;

  // Build report URL
  const reportUrl =
    tenantId && resolvedReportId
      ? `/o/${tenantId}/chemicals/reports/${resolvedReportId}`
      : undefined;

  // Build inventory URL
  const inventoryUrl =
    tenantId && resolvedFacilityId && reportingYearValue
      ? `/o/${tenantId}/chemicals/inventory/${resolvedFacilityId}?ry=${reportingYearValue}`
      : undefined;

  return {
    data,
    loading: activeQuery.loading,
    // Only the report query can surface SNAPSHOT_PENDING / ECSN-54123; the
    // facility query throws unrelated errors directly. Surfacing only the
    // report-side error keeps the shell's branching scoped to snapshot states.
    error: isUsingReport ? reportQuery.error : undefined,
    // True once snapshot polling has hit its hard timeout (report side only).
    snapshotPollingTimedOut: isUsingReport ? reportQuery.snapshotPollingTimedOut : false,

    isUsingReport,
    isUsingFacility,
    facilityId: resolvedFacilityId,
    reportingYear: reportingYearValue,
    reportId: resolvedReportId,
    reportUrl,
    inventoryUrl,
  };
}
